import { Box, Button, Link, Typography } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext } from "react";
import { routes } from "schemas/routes";

import { trackEvent } from "utils/tracking";

import { Icon } from "./shared/icons";
import { Callout, Pill } from "./shared/primitives";
import { buttonSx, g } from "./shared/tokens";

interface Props {
  title: string;
  description: string;
  infoBox: string | JSX.Element;
  completed?: boolean;
}

const CreateNicheTemplateCard = ({
  title,
  description,
  infoBox,
  completed,
}: Props) => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setCreateTemplateDialogOpen, outreachTemplates } = useContext(
    OutreachTemplatesContext,
  );

  const noTemplates = outreachTemplates.length === 0;

  const renderChip = () => {
    if (currentOrg?.isVaUser) {
      return (
        <Pill
          variant={completed ? "default" : "yellow"}
          icon={<Icon.Dollar size={12} />}
        >
          {completed ? "$10 earned" : "Earn $10"}
        </Pill>
      );
    }
    if (currentOrg?.isProUser) {
      return completed ? (
        <Pill variant="default" icon={<Icon.Check size={12} />}>
          Completed
        </Pill>
      ) : (
        <Pill variant="yellow" icon={<Icon.Sparkle size={12} />}>
          Highly recommended
        </Pill>
      );
    }
    return completed ? (
      <Pill variant="default" icon={<Icon.Check size={12} />}>
        50 email credits added
      </Pill>
    ) : (
      <Pill
        variant="yellow"
        sx={{
          px: "14px",
          py: "8px",
          fontSize: 13,
          flexShrink: 0,
          background: "#eed13b",
        }}
      >
        +50 email credits
      </Pill>
    );
  };

  return (
    <Box
      sx={{
        background: g.cardBg,
        border: `1px solid ${g.border}`,
        borderRadius: g.radiusLg,
        p: { xs: "16px", md: "22px" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "16px",
          flexWrap: "wrap",
          mb: "16px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "999px",
              background: completed ? g.green100 : g.primaryTint,
              color: completed ? g.successText : g.primary,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            {completed ? <Icon.Check size={18} /> : <Icon.Doc size={18} />}
          </Box>
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: g.text }}>
              Niche-Specific Template
            </Typography>
            <Typography sx={{ fontSize: 13, color: g.text2, mt: "2px" }}>
              Create a niche template to earn a one-time 50 bonus email credits
            </Typography>
          </Box>
        </Box>
        {renderChip()}
      </Box>

      <Callout tone="yellow" icon={<Icon.Sparkle size={16} />}>
        {infoBox}
      </Callout>

      <Box sx={{ mt: "18px" }}>
        {completed ? (
          <Typography sx={{ fontSize: 14, color: g.text2 }}>
            <Box
              component="span"
              sx={{
                color: g.successText,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                mr: "8px",
                fontWeight: 600,
              }}
            >
              <Icon.Check size={14} /> Niche template created!
            </Box>
            Visit the{" "}
            <Link
              sx={{ fontWeight: 600, textDecoration: "none" }}
              href={`/${routes.templates}`}
            >
              Templates tab
            </Link>{" "}
            to make edits.
          </Typography>
        ) : (
          <>
            <Button
              fullWidth
              disabled={noTemplates}
              sx={{ ...buttonSx.base, ...buttonSx.primary }}
              onClick={() => {
                trackEvent("Gamification - Create Niche Template");
                setCreateTemplateDialogOpen(true);
              }}
            >
              Create niche template{" "}
              {noTemplates ? (
                <Icon.Lock size={14} />
              ) : (
                <Icon.ArrowRight size={14} />
              )}
            </Button>

            {noTemplates && (
              <Typography
                sx={{
                  mt: "12px",
                  fontSize: 13,
                  color: g.textDisabled,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Icon.Lock size={14} />
                <span>
                  Please{" "}
                  <Link
                    sx={{
                      textDecoration: "none",
                      cursor: "pointer",
                      color: g.primary,
                      fontWeight: 700,
                    }}
                    onClick={() => {
                      trackEvent("Gamification - Create First Template");
                      setCreateTemplateDialogOpen(true);
                    }}
                  >
                    create a general template first
                  </Link>{" "}
                  to unlock this challenge.
                </span>
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default CreateNicheTemplateCard;
